import { Lock, Mail, User } from 'lucide-react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { register } from '../../actions/register';
import { HOSTED_REGISTER_PITCH } from '../../helpers/hostedOffer';
import useAuthRedirect from '../../hooks/useAuthRedirect';

const getErrorMessage = (error) => {
  if (!error) return '';
  if (error.msg) return error.msg;
  if (error.error) return error.error;
  if (Array.isArray(error.errors) && error.errors[0]?.msg) return error.errors[0].msg;
  return 'Unable to create your account. Check the form and try again.';
};

const splitFullName = (fullName) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const firstName = parts.shift() || '';
  const lastName = parts.join(' ') || firstName;
  return { firstName, lastName };
};

export default function Register() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  // Offer/membership the user signed up under, e.g. /register?offer=charter
  const offer = searchParams.get('offer') || undefined;
  const { isLoading, errorMessage } = useAuthRedirect('/sites', '/login');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [localError, setLocalError] = useState('');

  const onSubmit = (event) => {
    event.preventDefault();
    setLocalError('');

    if (!acceptedTerms) {
      setLocalError('You need to accept the terms before creating a workspace.');
      return;
    }

    if (password.length < 10) {
      setLocalError('Password must be at least 10 characters.');
      return;
    }

    const { firstName, lastName } = splitFullName(fullName);
    dispatch(register({
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      membership_type: offer,
    }));
  };

  const error = localError || getErrorMessage(errorMessage);

  return (
    <div className="auth-page">
      <section className="auth-side">
        <div className="auth-brand">
          <img src="/nc-logo-white.svg" alt="North Commerce" />
        </div>
        <div className="auth-pitch">
          <span className="auth-eyebrow">Start free - no card required</span>
          <h1>Spin up a staging store in 90 seconds.</h1>
          <p>{HOSTED_REGISTER_PITCH}</p>
        </div>
      </section>

      <main className="auth-form-panel">
        <div className="auth-meta">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>

        <div className="auth-form-wrap">
          <span className="auth-bracket">[ Create your account ]</span>
          <h2>Create your North workspace.</h2>
          <p>One workspace can host multiple staging sites. You only pay when a site goes live.</p>

          <form className="auth-form-body" onSubmit={onSubmit}>
            {error ? <div className="error-banner">{error}</div> : null}

            <div className="form-field">
              <label htmlFor="full-name">Full name</label>
              <div className="input-icon-wrap">
                <User size={16} />
                <input
                  id="full-name"
                  name="full-name"
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="email">Work email</label>
              <div className="input-icon-wrap">
                <Mail size={16} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="password">Password</label>
              <div className="input-icon-wrap">
                <Lock size={16} />
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>
              <span className="hint">At least 10 characters. No card required.</span>
            </div>

            <label className="check-line">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                required
              />
              <span>I agree to the Terms and Privacy Policy. Send me product updates.</span>
            </label>

            <button className="btn btn-primary full-width" type="submit" disabled={isLoading}>
              {isLoading ? 'Creating workspace...' : "Create workspace - it's free"}
            </button>
            <div className="auth-legal">
              No credit card required. Staging is free until you publish.
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
